import axios from 'axios';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconType } from 'react-icons';
import {
	FaAddressBook,
	FaBorderAll,
	FaChartPie,
	FaClipboardList,
	FaClock,
	FaCog,
	FaDesktop,
	FaDotCircle,
	FaFileImport,
	FaHome,
	FaMinus,
	FaNewspaper,
	FaRegNewspaper,
	FaSignOutAlt,
	FaUser,
	FaUserCheck,
	FaUsers,
	FaUserTie,
	FaUserTimes,
	FaWarehouse,
} from 'react-icons/fa';
import {
	FaBookOpenReader,
	FaCalendarDays,
	FaChartSimple,
	FaFileLines,
	FaListCheck,
	FaLocationDot,
	FaMoneyBill1,
	FaUserGroup,
} from 'react-icons/fa6';
import {
	MdOutlineKeyboardArrowDown,
	MdOutlineSpaceDashboard,
} from 'react-icons/md';
import logoHiGuard from '../../assets/images/HI-GUARD 2.png';
import logoKjri from '../../assets/images/logo-kjri-jb.png';
import { getToken, removeToken, removeUser } from '../../utils/handleToken';

const iconMapping: { [key: string]: IconType } = {
	// Menu Utama
	'fa-chart-simple': FaChartSimple,
	FaChartPie: FaChartPie,
	faHome: FaHome,
	'fa-desktop': FaDesktop,
	fadesktop: FaDesktop,
	'fa-users': FaUsers,
	'fa-money-bill-1': FaMoneyBill1,
	famoneybill1: FaMoneyBill1,
	'fa-book-open-reader': FaBookOpenReader,
	faBorderAll: FaBorderAll,
	'fa-border-all': FaBorderAll,

	// Sub Menu
	faHouse: FaWarehouse,

	// Master Data
	'-': FaDotCircle, // Default icon untuk yang tidak memiliki icon
	workhour: FaClock,
	location: FaLocationDot,
	employee: FaUser,
	customer: FaUserGroup,
	resident: FaUserTie,
	visitor: FaAddressBook,

	// HR Administration
	attendance: FaCalendarDays,
	leave: FaCalendarDays,
	timesheet: FaClipboardList,

	// HR Payroll
	'payslip-import': FaFileImport,
	'payslip-list': FaFileLines,

	// News
	'news-type': FaNewspaper,
	'news-create': FaRegNewspaper,
	'news-list': FaNewspaper,
};

interface MenuItem {
	id: string | number;
	permalink: string;
	title_en: string;
	title_id: string;
	icon: string;
	child: MenuItem[];
	// properti lain yang diperlukan
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
const URL_MENU = '/access/menu';

// const title = localStorage.getItem("title");

export default function Sidebar({
	isOpen,
	language,
}: {
	isOpen: boolean;
	language: string;
}) {
	const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
	const [expandedMenus, setExpandedMenus] = useState<number[]>([]);
	const pathname = usePathname();
	const [isSidebarDisabled, setIsSidebarDisabled] = useState(false);

	useEffect(() => {
		// Cek apakah sidebar harus dinonaktifkan
		const checkSidebarDisabled = () => {
			setIsSidebarDisabled(
				document.body.classList.contains('sidebar-disabled')
			);
		};

		// Jalankan pengecekan saat komponen dimount
		checkSidebarDisabled();

		// Buat observer untuk memantau perubahan class pada body
		const observer = new MutationObserver(checkSidebarDisabled);
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		});

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const fetchMenu = async () => {
			try {
				const token = getToken();
				if (!token) {
					// Jika token tidak ada, redirect ke login
					removeToken();
					removeUser();
					window.location.href = '/login';
					return;
				}

				const response = await axios.post(
					`${API_BASE_URL}${URL_MENU}`,
					{ req_from: process.env.NEXT_PUBLIC_NAME_REQ_FROM },
					{
						headers: {
							Authorization: token,
							'Content-Type': 'application/json',
						},
					}
				);
				if (response.data.status === 'success') {
					setMenuItems(response.data.data);
				} else {
					console.error('Gagal mengambil menu');
				}
			} catch (error) {
				console.error('Error fetching menu:', error);
			}
		};

		fetchMenu();
	}, []);

	const [styleTheme, setStyleTheme] = useState('');

	useEffect(() => {
		const host = window.location.host;
		if (host.includes('hi-guard.id') || host === 'localhost:3001') {
			setStyleTheme('STYLE 1');
		} else {
			setStyleTheme('STYLE 2');
		}
	}, []);

	const toggleMenu = (id: number) => {
		if (expandedMenus[0] === id) {
			setExpandedMenus([]);
		} else {
			setExpandedMenus([id]);
		}
	};

	const handleLogout = () => {
		removeToken();
		removeUser();
		window.location.href = '/login';
	};

	const isMenuActive = (permalink: string): boolean => {
		if (!permalink) return false;

		// Pastikan permalink selalu dimulai dengan "/"
		const normalizedPermalink = permalink.startsWith('/')
			? permalink
			: `/${permalink}`;

		// Cek apakah pathname saat ini cocok dengan permalink menu
		// Gunakan exact match untuk menghindari partial matching
		return pathname === normalizedPermalink;
	};

	const renderMenu = (menu: MenuItem, isChild?: boolean) => {
		const Icon =
			iconMapping[menu.icon] || (isChild ? FaMinus : MdOutlineSpaceDashboard);
		const isActive = isMenuActive(menu.permalink);

		// Pastikan permalink selalu dimulai dengan "/"
		const href = menu.permalink?.startsWith('/')
			? menu.permalink
			: `/${menu.permalink}`;

		return (
			<li key={menu.id}>
				{menu.child && menu.child.length > 0 ? (
					// Menu dengan child menggunakan div
					<div
						className={`relative flex justify-between items-center px-4 ${
							isChild ? 'py-[8px] text-sm' : 'py-[10px] text-md font-medium'
						} rounded-xl cursor-pointer overflow-hidden
						transition-all duration-500 ease-in-out
						${
							isActive
								? `bg-primary/80 text-white shadow-sm`
								: `text-slate-700 hover:text-primary`
						}
						before:absolute before:inset-0 before:bg-primary/20
						before:translate-x-[-101%] hover:before:translate-x-0
						before:transition-all before:duration-300 before:ease-in-out`}
						onClick={() => {
							if (!isChild) toggleMenu(Number(menu.id));
						}}
					>
						<div className="flex-1 flex items-center">
							{Icon && (
								<Icon
									className={`${isChild ? 'w-4 h-4' : 'w-5 h-5'} ${
										isActive ? `!text-white` : `text-primary`
									}`}
								/>
							)}
							<span
								className={`ml-3 flex-1 ${
									isActive
										? '!text-white'
										: `text-inherit`
								}`}
							>
								{language === 'en' ? menu.title_en : menu.title_id}
							</span>
						</div>
						<div className='-m-px'>
							<MdOutlineKeyboardArrowDown
							className={`w-6 h-6 transition-transform ${
								expandedMenus.includes(Number(menu.id)) ? 'rotate-180' : ''
							}`}
						/>
						</div>
					</div>
				) : (
					// Menu tanpa child menggunakan Link
					<Link href={href} className="block" replace={true}>
						<div
							className={`relative flex justify-between items-center px-4 ${
								isChild ? 'py-[8px] text-sm' : 'py-[10px] text-md font-medium'
							} rounded-xl cursor-pointer overflow-hidden
									transition-all duration-500 ease-in-out
									${
										isActive
											? `bg-primary/80 text-white shadow-sm`
											: `text-slate-700 hover:text-primary`
									}
									before:absolute before:inset-0 before:bg-primary/20
									before:translate-x-[-101%] hover:before:translate-x-0
									before:transition-all before:duration-300 before:ease-in-out`}
						>
							<div className="flex-1 flex items-center">
								{Icon && (
									<Icon
										className={`${isChild ? 'w-4 h-4' : 'w-5 h-5'} ${
											isActive ? '!text-white' : `text-primary`
										}`}
									/>
								)}
								<span
									className={`ml-3 flex-1 ${
										isActive
											? '!text-white'
											: `text-inherit`
									}`}
								>
									{language === 'en' ? menu.title_en : menu.title_id}
								</span>
							</div>
							{isActive && (
								<div className="lg:hidden w-1.5 h-1.5 rounded-full bg-white mr-1" />
							)}
						</div>
					</Link>
				)}

				{menu.child && menu.child.length > 0 && (
					<ul
						className={`ml-6 mt-2 space-y-[2px] transition-all duration-700 ease-in-out overflow-hidden ${
							expandedMenus.includes(Number(menu.id))
								? 'max-h-[500px]'
								: 'max-h-0'
						}`}
					>
						{expandedMenus.includes(Number(menu.id)) &&
							menu.child.map((childMenu: MenuItem) =>
								renderMenu(childMenu, true)
							)}
					</ul>
				)}
			</li>
		);
	};

	return (
		<div
			className={`
				fixed top-0 bottom-0 left-0 right-0 lg:right-auto
				h-full w-full lg:w-56 
				bg-white
				transform ${
					isOpen && !isSidebarDisabled ? 'translate-x-0' : '-translate-x-full'
				} transition-all duration-500 ease-in-out z-10
			`}
		>
			<div className="overflow-y-auto px-2 py-4 h-[calc(100%-80px)] mt-20 scrollsidebarClass">
				<ul className="space-y-1">
					{menuItems.map((menu) => renderMenu(menu))}
				</ul>
			</div>
		</div>
	);
}
